/** @jsx jsx */
import {
  Flex,
  Modal,
  ModalActionButton,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalHeading,
  ModalProps,
  Paragraph,
} from "@eworkit/iris";
import { Form, Formik } from "formik";
import { FC, useCallback, useContext, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDialogState } from "reakit/Dialog";
import { unstable_useId as useId } from "reakit/Id";
import { jsx } from "theme-ui";

import { UserContext } from "@/components/User";
import { formatFullName } from "@/data/persons";
import { TimeReport } from "@/data/time-reports";
import { useTimeReportMutations } from "@/data/time-reports/queries/useTimeReportMutations";
import { useToasts } from "@/views/components/Toast/useToasts";

import { NewTimeReportVersionModal } from "../edit/NewTimeReportVersionModal";
import {
  SubmitTimeReportFormFields,
  SubmitTimeReportFormValues,
  SUBMIT_TIME_REPORT_FORM_DEFAULT_VALUES,
  SUBMIT_TIME_REPORT_VALIDATION_SCHEMA,
} from "./SubmitTimeReportFormFields";

export const MODIFIED_BY_ANOTHER_USER_ERROR_CODE = "MODIFIED_BY_ANOTHER_USER";
export const CANNOT_BE_SUBMITTED_IN_CURRENT_STATE_ERROR_CODE = "CANNOT_BE_SUBMITTED_IN_CURRENT_STATE";

interface SubmitTimeReportModalProps extends ModalProps {
  timeReport: TimeReport;
  refetchTimeReport: () => Promise<unknown>;
}

export const SubmitTimeReportModal: FC<SubmitTimeReportModalProps> = ({
  timeReport,
  refetchTimeReport,
  ...modalProps
}) => {
  const { t } = useTranslation("session");
  const { addToast } = useToasts();
  const { contextConsultantProfileId } = useContext(UserContext);
  const { id: formId } = useId();

  const [loading, setLoading] = useState(false);

  const { submit } = useTimeReportMutations(timeReport.guid);

  const newTimeReportVersionModalState = useDialogState({ animated: true });

  const handleSubmit = useCallback(
    async (values: SubmitTimeReportFormValues) => {
      try {
        setLoading(true);

        await submit({
          externalComment: values.comment,
          attachments: values.attachments.filter(i => i.attachment).map(i => i.attachment!),
          lastReadDate: timeReport.lastReadDate,
        });

        modalProps.dialogState.hide?.();
      } catch (error) {
        if (error?.payload?.code === MODIFIED_BY_ANOTHER_USER_ERROR_CODE) {
          newTimeReportVersionModalState.show();
          modalProps.dialogState.hide();
        } else if (error?.payload?.code === CANNOT_BE_SUBMITTED_IN_CURRENT_STATE_ERROR_CODE) {
          addToast({
            text: t("TIME_REPORTS.SUBMIT.CANNOT_BE_SUBMITTED_ERROR_MESSAGE"),
            type: "warning",
          });
          await refetchTimeReport();
        } else {
          addToast({
            text: t("common:ERROR_MESSAGES.GENERAL"),
            type: "danger",
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [submit, modalProps, addToast, timeReport, t, newTimeReportVersionModalState, refetchTimeReport],
  );

  return (
    <React.Fragment>
      <Modal aria-label={t("TIME_REPORTS.SUBMIT.SUBMIT_TIME_REPORT")} {...modalProps}>
        <ModalContainer>
          <ModalHeader
            onCloseClick={modalProps.dialogState.hide}
            closeButtonAriaLabel={t("COMPONENTS.MODAL.CLOSE")}
            headerPlaceholder={
              <Flex flexDirection="column" justifyContent="flex-start" gap="sm">
                <div>
                  <ModalHeading>{t("TIME_REPORTS.SUBMIT.SUBMIT_TIME_REPORT")}</ModalHeading>
                </div>
                <Paragraph color="n700">
                  {timeReport.worker.profileId !== contextConsultantProfileId
                    ? t("TIME_REPORTS.SUBMIT.YOU_ARE_ABOUT_TO_SUBMIT_ON_BEHALF", {
                        consultant: formatFullName(timeReport.worker),
                      })
                    : t("TIME_REPORTS.SUBMIT.YOU_ARE_ABOUT_TO_SUBMIT")}
                </Paragraph>
              </Flex>
            }
          />

          <ModalContent>
            <Formik<SubmitTimeReportFormValues>
              initialValues={SUBMIT_TIME_REPORT_FORM_DEFAULT_VALUES}
              validationSchema={SUBMIT_TIME_REPORT_VALIDATION_SCHEMA}
              onSubmit={handleSubmit}>
              {formik => (
                <Form id={formId}>
                  <SubmitTimeReportFormFields formik={formik} />
                </Form>
              )}
            </Formik>
          </ModalContent>

          <ModalFooter>
            <ModalActionButton type="button" variant="ghost" onClick={modalProps.dialogState.hide}>
              {t("TIME_REPORTS.SUBMIT.CANCEL")}
            </ModalActionButton>
            <ModalActionButton
              type="submit"
              form={formId}
              variant="primary"
              loading={loading}
              disabled={loading}
              focusable={!loading}>
              {t("TIME_REPORTS.SUBMIT.SUBMIT_TIME_REPORT")}
            </ModalActionButton>
          </ModalFooter>
        </ModalContainer>
      </Modal>

      {newTimeReportVersionModalState.visible && (
        <NewTimeReportVersionModal dialogState={newTimeReportVersionModalState} refetchTimeReport={refetchTimeReport} />
      )}
    </React.Fragment>
  );
};
